import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Github, DollarSign, Mail, GraduationCap, ArrowRight, X } from 'lucide-react';
import { projectsData } from '../data/mock';

// Warm color mapping - no blue, violet, or green
const colorMap = {
  '#88a2ff': '#e8c4c4',  // Dusty rose (was blue)
  '#d987ff': '#f4b8c5',  // Soft rose (was violet)
  '#78d692': '#f5d89a'   // Soft gold (was green)
};

const getGentleColor = (color) => colorMap[color] || color;

function ProjectIcon({ iconName, color, size = 28 }) {
  const props = { size, style: { color: getGentleColor(color) } };
  switch (iconName) {
    case 'DollarSign': return <DollarSign {...props} />;
    case 'Mail': return <Mail {...props} />;
    case 'GraduationCap': return <GraduationCap {...props} />;
    default: return <DollarSign {...props} />;
  }
}

function ProjectCard({ project, idx, inView, onClick }) {
  const gentleColor = getGentleColor(project.color);
  const techPreview = project.techStack.slice(0, 3);
  const extraCount = project.techStack.length - 3;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: idx * 0.15 }}
      whileHover={{ y: -8 }}
      onClick={onClick}
      className="group cursor-pointer"
    >
      <div
        className="h-full rounded-2xl p-8 border border-white/10 hover:border-transparent transition-all duration-300 relative overflow-hidden backdrop-blur-sm"
        style={{ background: `linear-gradient(135deg, ${gentleColor}08, ${gentleColor}03)` }}
      >
        {/* Hover glow */}
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: `radial-gradient(circle at 50% 50%, ${gentleColor}15, transparent 70%)` }}
        />

        <div className="relative z-10">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
            style={{ background: `linear-gradient(135deg, ${gentleColor}25, ${gentleColor}10)` }}
          >
            <ProjectIcon iconName={project.icon} color={project.color} />
          </div>

          <h3 className="text-2xl font-semibold text-white mb-2">{project.title}</h3>
          <p className="text-white/50 mb-6">{project.shortDesc}</p>

          <div className="flex flex-wrap gap-2 mb-6">
            {techPreview.map((tech) => (
              <span key={tech} className="px-3 py-1 text-xs rounded-full bg-white/5 border border-white/10 text-white/60">
                {tech}
              </span>
            ))}
            {extraCount > 0 && (
              <span className="px-3 py-1 text-xs rounded-full bg-white/5 border border-white/10 text-white/40">
                +{extraCount}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm font-medium" style={{ color: gentleColor }}>
            <span>Learn more</span>
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ProjectModal({ project, onClose }) {
  if (!project) return null;
  const gentleColor = getGentleColor(project.color);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-[#12121a] border border-white/10 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 relative" style={{ background: `linear-gradient(135deg, ${gentleColor}15 0%, transparent 100%)` }}>
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
            <X size={20} className="text-white" />
          </button>
          
          <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-4" style={{ background: `linear-gradient(135deg, ${gentleColor}30, ${gentleColor}15)` }}>
            <ProjectIcon iconName={project.icon} color={project.color} size={32} />
          </div>
          
          <h2 className="text-3xl font-bold text-white mb-2">{project.title}</h2>
          <p className="text-white/50">{project.shortDesc}</p>
        </div>

        <div className="p-8 pt-0">
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-3">About</h3>
            <p className="text-white/60 leading-relaxed">{project.description}</p>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-3">Key Features</h3>
            <ul className="space-y-2">
              {project.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-white/60">
                  <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: gentleColor }} />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-3">Tech Stack</h3>
            <div className="flex flex-wrap gap-2">
              {project.techStack.map((t) => (
                <span key={t} className="px-4 py-2 text-sm rounded-full border" style={{ background: `${gentleColor}10`, borderColor: `${gentleColor}25`, color: gentleColor }}>{t}</span>
              ))}
            </div>
          </div>

          <motion.a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-4 rounded-full font-semibold flex items-center justify-center gap-2 transition-all hover:shadow-lg"
            style={{ background: `linear-gradient(135deg, ${gentleColor}, ${gentleColor}cc)`, color: '#0d0d0f' }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Github size={18} />
            View on GitHub
          </motion.a>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function ProjectsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [selected, setSelected] = useState(null);

  return (
    <section id="projects" ref={ref} className="py-32 bg-[#0a0a0c] relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-br from-[#ffb79c]/5 via-[#f5d89a]/5 to-[#f4b8c5]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Featured Projects</h2>
          <p className="text-white/50 max-w-2xl mx-auto">Here are some projects I'm proud of. Each represents a unique challenge.</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex gap-4 items-start max-w-2xl mb-12"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#ffb79c] to-[#f5d89a] flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-[#0d0d0f]">SP</span>
          </div>
          <div className="bg-gradient-to-r from-[#ffb79c]/10 to-[#f5d89a]/5 border border-[#ffb79c]/15 rounded-2xl rounded-tl-md px-5 py-3 backdrop-blur-sm">
            <p className="text-white/80">Click on any card to dive deeper into the details!</p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projectsData.map((p, i) => (
            <ProjectCard key={p.id} project={p} idx={i} inView={inView} onClick={() => setSelected(p)} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
